import React from 'react';
import { FormattedMessage } from 'react-intl';

import BaseInput from './BaseInput';

import { fontSize, colors } from 'styles/cp';

// Credit floor percentage constants
export const CREDIT_FLOOR_PERCENTAGES = [0.5, 0.75] as const;

export interface CreditFloorEditInputProps {
  // Input props
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled: boolean;

  // Currency symbol props
  currencySymbol?: string;
  currencySymbolPosition?: 'BEFORE' | 'AFTER';

  // Percentage button props
  currentAmount: number;
  fiftyPercentAmount: number;
  seventyFivePercentAmount: number;
  isOtherSelected: boolean;
  onPercentageClick: (percentage: number) => void;
  onOtherClick: () => void;
  onResetClick: () => void;
}

const CreditFloorEditInput: React.FC<CreditFloorEditInputProps> = ({
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  disabled,
  currencySymbol,
  currencySymbolPosition = 'BEFORE',
  currentAmount,
  fiftyPercentAmount,
  seventyFivePercentAmount,
  isOtherSelected,
  onPercentageClick,
  onOtherClick,
  onResetClick,
}) => {
  const isPercentageActive = (targetAmount: number) =>
    !isOtherSelected && currentAmount === targetAmount;

  const isOtherActive =
    isOtherSelected ||
    (currentAmount !== fiftyPercentAmount && currentAmount !== seventyFivePercentAmount);

  const percentageBtnClass = (active: boolean) => `percentage-btn${active ? ' active' : ''}`;

  // Format currency symbol: add space after multi-character symbols for visual clarity
  const formattedSymbol =
    currencySymbol && currencySymbol.length > 1 ? `${currencySymbol} ` : currencySymbol;
  return (
    <div className="credit-floor-edit-wrapper">
      <div
        className={`credit-floor-edit-input-border ${
          currencySymbolPosition === 'AFTER' ? 'symbol-after' : ''
        }`}
      >
        {formattedSymbol && <span className="currency-symbol">{formattedSymbol}</span>}
        <BaseInput
          type="text"
          inputMode="decimal"
          value={value}
          onKeyDown={onKeyDown}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          fontSize={fontSize.xs}
          pattern="[0-9]*[.]?[0-9]{0,2}"
          autoFocus
          disabled={disabled}
        />
      </div>
      <span className="credit-floor-percentage-buttons">
        <button
          type="button"
          className={percentageBtnClass(isPercentageActive(fiftyPercentAmount))}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPercentageClick(CREDIT_FLOOR_PERCENTAGES[0])}
        >
          50%
        </button>
        <button
          type="button"
          className={percentageBtnClass(isPercentageActive(seventyFivePercentAmount))}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPercentageClick(CREDIT_FLOOR_PERCENTAGES[1])}
        >
          75%
        </button>
        <button
          type="button"
          className={percentageBtnClass(isOtherActive)}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onOtherClick}
        >
          <FormattedMessage id="PAYFLOW_OTHER" defaultMessage="Other" />
        </button>
        <button
          type="button"
          className="credit-floor-reset-link"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onResetClick}
        >
          <FormattedMessage id="PAYFLOW_RESET" defaultMessage="Reset" />
        </button>
      </span>
      <style jsx>{`
        /* TreatmentB credit floor edit mode wrapper */
        .credit-floor-edit-wrapper {
          display: block;
          :global(input) {
            margin-bottom: 2px;
            font-size: ${fontSize.xs};
            font-family: AvenirNextforINTUIT-Regular;
            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button {
              appearance: none;
              margin: 0;
            }
          }
        }

        /* Border wrapper around input only (not buttons) */
        .credit-floor-edit-input-border {
          display: flex;
          align-items: center;
          flex-direction: row;
          border-radius: 6px;
          border: 1px solid ${colors.stoneWhite};
          background: #ffffff;
          padding: 8px 16px;
        }

        /* Reverse flex direction when symbol is positioned after */
        .credit-floor-edit-input-border.symbol-after {
          flex-direction: row-reverse;
        }

        /* Currency symbol styling */
        .currency-symbol {
          font-size: ${fontSize.xs};
          font-family: AvenirNextforINTUIT-Regular;
          color: ${colors.gray02};
          user-select: none;
          flex-shrink: 0;
          margin-right: 8px;
        }

        .credit-floor-edit-input-border.symbol-after .currency-symbol {
          margin-right: 0;
          margin-left: 8px;
        }

        /* TreatmentB credit floor percentage buttons and reset link row */
        .credit-floor-percentage-buttons {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 12px;
        }

        .percentage-btn {
          padding: 4px 10px;
          border: 1px solid ${colors.green05};
          border-radius: 4px;
          background: transparent;
          cursor: pointer;
          font-family: inherit;
          font-size: ${fontSize.xxs};
          color: ${colors.green05};
          min-width: 40px;
          min-height: 24px;
          transition: background-color 0.2s;
        }

        .percentage-btn:hover {
          background: ${colors.whiteGreen};
        }

        .percentage-btn.active {
          border-color: ${colors.green05};
          background: rgba(0, 137, 46, 0.2);
        }

        /* TreatmentB credit floor reset link - styled as text link per Figma */
        .credit-floor-reset-link {
          background: none;
          border: none;
          cursor: pointer;
          font-family: inherit;
          font-size: ${fontSize.xs};
          color: ${colors.gray02};
          padding: 0;
          margin-left: auto;
          line-height: 21px;
        }

        .credit-floor-reset-link:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default CreditFloorEditInput;
